import { computed, ref, type ComputedRef } from 'vue'

import { billingApi } from '../api/BillingApi'
import { BillingCheckoutFlow } from '../flows/BillingCheckoutFlow'
import type { BillingPackageDto, PricingQuote, StartCheckoutParams } from '../models/billing.types'
import { useCreditStore } from '../stores/creditStore'

import { getAccountDetailPackageId, useAccount, useWorkspace } from '@/features/account'
import { useEnv } from '@/shared/config'

export interface UseBilling {
  packages: ComputedRef<BillingPackageDto[]>
  quote: ComputedRef<PricingQuote | null>
  quoteLoading: ComputedRef<boolean>
  loading: ComputedRef<boolean>
  error: ComputedRef<boolean>
  isSelfServeEnabled: ComputedRef<boolean>
  loadPlans: (force?: boolean) => Promise<void>
  startCheckout: (params: StartCheckoutParams) => Promise<void>
}

export function useBilling(): UseBilling {
  const store = useCreditStore()
  const account = useAccount()
  const workspace = useWorkspace()
  const quote = ref<PricingQuote | null>(null)
  const quoteLoading = ref(false)

  async function loadPlans(force = false): Promise<void> {
    await store.fetchPricingPackages(force)
    const accountId = account.accountId.value
    if (accountId === undefined) return
    quoteLoading.value = true
    try {
      quote.value = await billingApi.getLatestPricingQuote(accountId).catch(() => null)
    } finally {
      quoteLoading.value = false
    }
  }

  async function startCheckout(params: StartCheckoutParams): Promise<void> {
    const accountId = account.accountId.value
    if (accountId === undefined) throw new Error('Checkout requires a selected account')
    const workspaceId = Number(workspace.workspaceId.value)

    await new BillingCheckoutFlow().execute({
      ...params,
      accountId,
      ...(Number.isInteger(workspaceId) ? { workspaceId } : {}),
      currentPackageId: getAccountDetailPackageId(account.accountDetail.value),
      isShopify: account.accountDetail.value?.user?.is_shopify === true,
    })
  }

  return {
    packages: computed(() => store.pricingPackages),
    quote: computed(() => quote.value),
    quoteLoading: computed(() => quoteLoading.value),
    loading: computed(() => store.pricingPackagesLoading),
    error: computed(() => store.pricingPackagesError),
    // Contract 11 keeps the future POST-session endpoint dark by default. Environments may turn
    // the presentation on only after BE-11-06 is contract-tested; hosted plan checkout remains
    // the explicit `startCheckout()` path above.
    isSelfServeEnabled: computed(() => useEnv().CREDIT_SELF_SERVE_ENABLED),
    loadPlans,
    startCheckout,
  }
}
